import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Clock, Trophy, Play, CheckCircle, Star, Filter,
  Receipt, Wallet, Home, TrendingUp, Briefcase, CreditCard,
  Sunset, Shield, Brain, GraduationCap, Video, Bell, Sparkles,
  ChevronRight, X, Lock, FileText, ChevronDown, ChevronUp, Image
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  'receipt': Receipt,
  'piggy-bank': Wallet,
  'wallet': Wallet,
  'home': Home,
  'trending-up': TrendingUp,
  'briefcase': Briefcase,
  'credit-card': CreditCard,
  'sunset': Sunset,
  'shield': Shield,
  'brain': Brain,
  'graduation-cap': GraduationCap,
  'default': BookOpen
};

const getIcon = (iconName) => {
  return iconMap[iconName] || iconMap['default'];
};

export default function Courses() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCourses();
    }
  }, [selectedCategory, selectedDifficulty, token]);

  const fetchData = async () => {
    try {
      const [categoriesRes, coursesRes, webinarsRes, statsRes] = await Promise.all([
        axios.get(`${API}/courses/categories`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/courses`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/webinars`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/courses/stats/overview`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      setCategories(categoriesRes.data.categories);
      setCourses(coursesRes.data.courses);
      setWebinars(webinarsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      let url = `${API}/courses`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }});
      setCourses(response.data.courses);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const openCourseDetail = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCourse(response.data);
      
      // Auto-expand first uncompleted lesson
      const firstUncompleted = response.data.lessons?.find(l => !l.is_completed);
      if (firstUncompleted) {
        setExpandedLesson(firstUncompleted.id);
      } else if (response.data.lessons?.length > 0) {
        setExpandedLesson(response.data.lessons[0].id);
      }
    } catch (error) {
      toast.error('Failed to load course details');
    }
  };

  const completeLesson = async (courseId, lessonId) => {
    try {
      const response = await axios.post(
        `${API}/courses/${courseId}/lessons/${lessonId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(`+${response.data.xp_earned} XP earned!`);
      
      if (response.data.course_completed) {
        toast.success('Course completed!', { duration: 5000 });
      }
      
      // Refresh course detail and stats
      openCourseDetail(courseId);
      fetchData();
    } catch (error) {
      toast.error('Failed to complete lesson');
    }
  };

  const registerWebinar = async (webinarId) => {
    try {
      const response = await axios.post(
        `${API}/webinars/${webinarId}/register`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to register');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-400 bg-green-400/20';
      case 'intermediate': return 'text-yellow-400 bg-yellow-400/20';
      case 'advanced': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-[#C0975C] text-xl animate-pulse">Loading courses...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-black mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <span className="text-[#C0975C]">LEARN</span> & GROW
            </h1>
            <p className="text-[#A3A3A3]">Structured courses for South Africans building wealth</p>
          </div>
          
          {stats && (
            <div className="flex gap-4">
              <div className="neo-card px-4 py-2 text-center">
                <div className="text-2xl font-black text-[#C0975C]">{stats.courses_completed}</div>
                <div className="text-xs text-[#A3A3A3]">Completed</div>
              </div>
              <div className="neo-card px-4 py-2 text-center">
                <div className="text-2xl font-black text-[#C0975C]">{stats.lessons_completed}</div>
                <div className="text-xs text-[#A3A3A3]">Lessons</div>
              </div>
            </div>
          )}
        </div>

        {/* Sponsored Webinars Banner */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden neo-card p-6 border-2 border-[#0A1F44]"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#0A1F44]/10 rounded-full blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#0A1F44]/20 flex items-center justify-center">
                <Video className="w-8 h-8 text-[#0A1F44]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-[#0A1F44] text-black text-xs font-bold">COMING SOON</span>
                  <Sparkles className="w-4 h-4 text-[#C0975C]" />
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Sponsored Webinars
                </h3>
                <p className="text-[#A3A3A3] text-sm">
                  Live expert sessions on investing, tax, business & more
                </p>
              </div>
            </div>
            <button 
              onClick={() => toast.success('You\'ll be notified when webinars launch!')}
              className="neo-button px-6 py-3 bg-[#0A1F44] text-black font-bold hover:bg-[#0A1F44]/80 flex items-center gap-2"
            >
              <Bell className="w-4 h-4" />
              Notify Me
            </button>
          </div>
          
          {/* Upcoming Webinars Preview */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {webinars.webinars?.slice(0, 3).map((webinar) => (
              <div key={webinar.id} className="bg-white/5 p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-[#0A1F44]/20 flex items-center justify-center flex-shrink-0">
                  <Video className="w-5 h-5 text-[#0A1F44]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm truncate">{webinar.title}</h4>
                  <p className="text-xs text-[#A3A3A3] mt-1">{webinar.speaker}</p>
                  <span className="inline-block mt-2 px-2 py-0.5 bg-[#C0975C]/20 text-[#C0975C] text-xs">
                    {webinar.date}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              !selectedCategory ? 'bg-[#C0975C] text-black' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            All Courses
          </button>
          {categories.map((cat) => {
            const Icon = getIcon(cat.icon);
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id === selectedCategory ? null : cat.id)}
                className={`px-4 py-2 text-sm font-bold transition-colors flex items-center gap-2 ${
                  selectedCategory === cat.id 
                    ? 'text-black' 
                    : 'bg-white/10 hover:bg-white/20'
                }`}
                style={selectedCategory === cat.id ? { backgroundColor: cat.color } : {}}
              >
                <Icon className="w-4 h-4" />
                {cat.name}
                <span className="text-xs opacity-60">({cat.course_count})</span>
              </button>
            );
          })}
        </div>

        {/* Difficulty Filter */}
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-[#A3A3A3]" />
          <div className="flex gap-2">
            {['beginner', 'intermediate', 'advanced'].map((diff) => (
              <button
                key={diff}
                onClick={() => setSelectedDifficulty(diff === selectedDifficulty ? null : diff)}
                className={`px-3 py-1 text-xs font-bold capitalize transition-colors ${
                  selectedDifficulty === diff
                    ? getDifficultyColor(diff)
                    : 'bg-white/5 text-[#A3A3A3] hover:bg-white/10'
                }`}
              >
                {diff}
              </button>
            ))}
          </div>
        </div>

        {/* Course Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, index) => {
            const Icon = getIcon(course.icon);
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => openCourseDetail(course.id)}
                className="neo-card p-6 cursor-pointer hover:border-[#C0975C] transition-colors group"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 flex items-center justify-center"
                    style={{ backgroundColor: `${course.color}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: course.color }} />
                  </div>
                  <span className={`px-2 py-1 text-xs font-bold capitalize ${getDifficultyColor(course.difficulty)}`}>
                    {course.difficulty}
                  </span>
                </div>

                {/* Title & Description */}
                <h3 className="font-bold text-lg mb-2 group-hover:text-[#C0975C] transition-colors">
                  {course.title}
                </h3>
                <p className="text-[#A3A3A3] text-sm mb-4 line-clamp-2">
                  {course.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {course.tags?.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-white/5 text-xs text-[#A3A3A3]">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-[#A3A3A3] mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {course.duration_mins} min
                  </div>
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    {course.lessons_count} lessons
                  </div>
                  <div className="flex items-center gap-1">
                    <Trophy className="w-4 h-4 text-[#C0975C]" />
                    {course.xp_reward} XP
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="relative h-2 bg-white/10">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${course.progress_percent}%` }}
                    className="absolute h-full"
                    style={{ backgroundColor: course.color }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-[#A3A3A3]">
                    {course.completed_lessons}/{course.lessons_count} complete
                  </span>
                  {course.is_completed ? (
                    <span className="text-[#C0975C] flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Done
                    </span>
                  ) : (
                    <span style={{ color: course.color }}>
                      {Math.round(course.progress_percent)}%
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {courses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-[#A3A3A3] mx-auto mb-4" />
            <p className="text-[#A3A3A3]">No courses found with selected filters</p>
          </div>
        )}

        {/* Course Detail Modal */}
        <AnimatePresence>
          {selectedCourse && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
              onClick={() => { setSelectedCourse(null); setExpandedLesson(null); }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="neo-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-16 h-16 flex items-center justify-center"
                      style={{ backgroundColor: `${selectedCourse.color}20` }}
                    >
                      {(() => {
                        const Icon = getIcon(selectedCourse.icon);
                        return <Icon className="w-8 h-8" style={{ color: selectedCourse.color }} />;
                      })()}
                    </div>
                    <div>
                      <h2 className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {selectedCourse.title}
                      </h2>
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`px-2 py-1 text-xs font-bold capitalize ${getDifficultyColor(selectedCourse.difficulty)}`}>
                          {selectedCourse.difficulty}
                        </span>
                        <span className="text-[#A3A3A3] text-sm flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {selectedCourse.duration_mins} min
                        </span>
                        <span className="text-[#C0975C] text-sm flex items-center gap-1">
                          <Trophy className="w-4 h-4" /> {selectedCourse.xp_reward} XP
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedCourse(null); setExpandedLesson(null); }}
                    className="text-[#A3A3A3] hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Description */}
                <p className="text-[#A3A3A3] mb-6">{selectedCourse.description}</p>

                {/* Progress */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold">Your Progress</span>
                    <span style={{ color: selectedCourse.color }}>
                      {Math.round(selectedCourse.progress_percent)}%
                    </span>
                  </div>
                  <div className="relative h-3 bg-white/10">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${selectedCourse.progress_percent}%` }}
                      className="absolute h-full"
                      style={{ backgroundColor: selectedCourse.color }}
                    />
                  </div>
                </div>

                {/* Lessons */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">Lessons ({selectedCourse.lessons?.length || 0})</h3>
                    <span className="text-xs text-[#A3A3A3] flex items-center gap-1">
                      <ChevronDown className="w-3 h-3" /> Tap to expand
                    </span>
                  </div>
                  {selectedCourse.lessons?.map((lesson, index) => (
                    <div key={lesson.id} className="border border-white/10">
                      {/* Lesson Header */}
                      <div
                        className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                          lesson.is_completed ? 'bg-[#C0975C]/10' : 'bg-white/5 hover:bg-white/10'
                        }`}
                        onClick={() => setExpandedLesson(expandedLesson === lesson.id ? null : lesson.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 flex items-center justify-center ${
                            lesson.is_completed ? 'bg-[#C0975C] text-black' : 'bg-white/10'
                          }`}>
                            {lesson.is_completed ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <span className="font-bold">{index + 1}</span>
                            )}
                          </div>
                          <div>
                            <h4 className={`font-bold ${lesson.is_completed ? 'text-[#C0975C]' : ''}`}>
                              {lesson.title}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-[#A3A3A3]">
                              {lesson.content_type === 'article' && <FileText className="w-3 h-3" />}
                              {lesson.content_type === 'video' && <FileText className="w-3 h-3" />}
                              {lesson.content_type === 'interactive' && <Brain className="w-3 h-3" />}
                              {lesson.content_type === 'quiz' && <Trophy className="w-3 h-3" />}
                              <span className="capitalize">{lesson.content_type === 'video' ? 'article' : lesson.content_type}</span>
                              <span>•</span>
                              <span>{lesson.duration_mins} min read</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {expandedLesson === lesson.id ? (
                            <ChevronUp className="w-5 h-5 text-[#A3A3A3]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#A3A3A3]" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Lesson Content */}
                      <AnimatePresence>
                        {expandedLesson === lesson.id && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 bg-black/30 border-t border-white/10">
                              {/* Placeholder Image */}
                              <div className="bg-gradient-to-br from-white/5 to-white/10 h-40 mb-4 flex items-center justify-center">
                                <div className="text-center">
                                  <Image className="w-12 h-12 text-[#A3A3A3] mx-auto mb-2" />
                                  <span className="text-[#525252] text-sm">Course illustration</span>
                                </div>
                              </div>
                              
                              {/* Lesson Text Content */}
                              <div className="prose prose-invert max-w-none">
                                <LessonContent 
                                  courseTitle={selectedCourse.title} 
                                  lessonTitle={lesson.title}
                                  lessonIndex={index}
                                />
                              </div>
                              
                              {/* Mark Complete Button */}
                              {!lesson.is_completed && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    completeLesson(selectedCourse.id, lesson.id);
                                  }}
                                  className="mt-6 w-full neo-button py-3 bg-[#C0975C] text-black font-bold flex items-center justify-center gap-2"
                                >
                                  <CheckCircle className="w-5 h-5" />
                                  Mark as Complete
                                </button>
                              )}
                              
                              {lesson.is_completed && (
                                <div className="mt-6 p-3 bg-[#C0975C]/10 text-center">
                                  <span className="text-[#C0975C] font-bold flex items-center justify-center gap-2">
                                    <CheckCircle className="w-5 h-5" />
                                    Lesson Completed!
                                  </span>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                  
                  {(!selectedCourse.lessons || selectedCourse.lessons.length === 0) && (
                    <div className="text-center py-8 text-[#A3A3A3]">
                      <Lock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Lesson content coming soon!</p>
                      <p className="text-sm">Check back later for full course material.</p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {selectedCourse.tags?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="flex flex-wrap gap-2">
                      {selectedCourse.tags.map((tag) => (
                        <span key={tag} className="px-3 py-1 bg-white/5 text-xs text-[#A3A3A3]">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}

// Component to generate lesson content based on course and lesson titles
function LessonContent({ courseTitle, lessonTitle, lessonIndex }) {
  // Generate relevant content based on course and lesson
  const getContent = () => {
    const lowerCourse = courseTitle.toLowerCase();
    const lowerLesson = lessonTitle.toLowerCase();
    
    // ============ TAX CONTENT ============
    if (lowerCourse.includes('sars') || (lowerCourse.includes('tax') && !lowerCourse.includes('tfsa'))) {
      if (lowerLesson.includes('what is sars') || lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What is SARS?</h4>
            <p className="text-[#A3A3A3] mb-4">
              The South African Revenue Service (SARS) is the government agency responsible for 
              collecting taxes in South Africa. Think of them as the tax collectors who ensure 
              everyone contributes their fair share to fund public services like roads, schools, 
              and hospitals.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Key Points:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>SARS collects income tax, VAT, and other taxes</li>
                <li>Every working South African needs a tax number</li>
                <li>Tax season runs from July to November each year</li>
                <li>You can register for eFiling at sars.gov.za</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Understanding SARS is your first step to being financially responsible. 
              Don't worry - paying taxes means you're earning money!
            </p>
          </>
        );
      }
      if (lowerLesson.includes('income tax') || lessonIndex === 1) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Income Tax Basics</h4>
            <p className="text-[#A3A3A3] mb-4">
              Income tax is a percentage of your earnings that goes to the government. 
              The more you earn, the higher percentage you pay - this is called a progressive tax system.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">2026/2027 Tax Brackets (effective 1 March 2026):</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>R0 to R245,100: 18% of taxable income</li>
                <li>R245,101 to R383,100: R44,118 + 26% above R245,100</li>
                <li>R383,101 to R530,200: R79,998 + 31% above R383,100</li>
                <li>R530,201 to R695,800: R125,599 + 36% above R530,200</li>
                <li>R695,801 to R887,000: R185,215 + 39% above R695,800</li>
                <li>R887,001 to R1,878,600: R259,783 + 41% above R887,000</li>
                <li>R1,878,601+: R666,339 + 45% above R1,878,600</li>
              </ul>
              <p className="text-xs text-[#A3A3A3] mt-2">Primary rebate: R17,820 · Secondary (65+): R9,765 · Tertiary (75+): R3,249 · Source: SARS Budget 2026</p>
            </div>
            <p className="text-[#A3A3A3]">
              Good news: If you earn under R99,000 per year (under 65), you don't pay income tax!
              For 65 to 74 the threshold is R153,250, and for 75+ it's R171,300.
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Understanding taxes is crucial for every South African. Whether you're employed 
            or running a side business, knowing how tax works helps you keep more of your money legally.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Tax Tips for Earners:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Register for eFiling - it's free and makes tax easy</li>
              <li>Keep records of all income and expenses</li>
              <li>Claim deductions you're entitled to</li>
              <li>File on time to avoid penalties</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Pro tip: If you're freelancing or have a side business, you can deduct business 
            expenses from your taxable income!
          </p>
        </>
      );
    }
    
    // ============ TFSA CONTENT ============
    if (lowerCourse.includes('tfsa') || lowerCourse.includes('tax-free savings')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What is a TFSA?</h4>
            <p className="text-[#A3A3A3] mb-4">
              A Tax-Free Savings Account (TFSA) is one of the best investment tools available 
              to South Africans. Any growth, dividends, or interest earned in your TFSA is 
              completely tax-free - forever!
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Why TFSAs are Amazing:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>Zero tax on growth, dividends, or interest</li>
                <li>R46,000 maximum contribution per year (raised in Budget 2026, from 1 March 2026)</li>
                <li>R500,000 lifetime contribution limit</li>
                <li>Withdraw anytime (but withdrawals don't restore your contribution room)</li>
                <li>Excess contributions are taxed at 40%, so stay within limits!</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Example: If you invest R46,000/year for 11 years (R506,000 total) and it grows
              to R1.5 million, you pay ZERO tax on the R1 million profit!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Your TFSA should be one of your first investment priorities as a South African. 
            The earlier you start, the more time your money has to grow tax-free.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Where to Open a TFSA:</h5>
            <p className="text-[#A3A3A3]">
              Many South African banks, investment platforms and low-cost ETF providers offer tax-free savings accounts.
              Minimums, fees and fund choices differ between providers, so compare them carefully. A Precision Wealth
              adviser can help you choose an option that suits your goals.
            </p>
          </div>
          <p className="text-[#A3A3A3]">
            Pro tip: Put your TFSA money in ETFs for long-term growth rather than just 
            cash savings accounts!
          </p>
        </>
      );
    }
    
    // ============ CREDIT SCORE CONTENT ============
    if (lowerCourse.includes('credit score')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What is a Credit Score?</h4>
            <p className="text-[#A3A3A3] mb-4">
              Your credit score is like your financial report card. It's a number (usually 300-900) 
              that tells lenders how trustworthy you are with money. A higher score means better 
              loan rates and easier approvals.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Credit Score Ranges:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-red-400">300-579:</span> Poor - Very difficult to get credit</li>
                <li><span className="text-yellow-400">580-669:</span> Fair - Higher interest rates</li>
                <li><span className="text-[#C0975C]">670-739:</span> Good - Decent rates available</li>
                <li><span className="text-green-400">740-900:</span> Excellent - Best rates and terms</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Fun fact: You can check your credit score for FREE at ClearScore, 
              Credit Karma SA, or directly from TransUnion!
            </p>
          </>
        );
      }
      if (lessonIndex === 1) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What Affects Your Credit Score?</h4>
            <p className="text-[#A3A3A3] mb-4">
              Your credit score is calculated based on several factors. Understanding these 
              helps you improve your score strategically.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Credit Score Factors:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-[#C0975C]">35%</span> - Payment history (pay on time!)</li>
                <li><span className="text-[#0A1F44]">30%</span> - Credit utilization (don't max out cards)</li>
                <li><span className="text-[#C0975C]">15%</span> - Length of credit history</li>
                <li><span className="text-yellow-400">10%</span> - Types of credit</li>
                <li><span className="text-purple-400">10%</span> - New credit inquiries</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              The #1 rule: ALWAYS pay at least the minimum on time. One late payment 
              can drop your score by 100+ points!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Building good credit early in life gives you a huge advantage. You'll get 
            better rates on car loans, home loans, and even phone contracts.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Quick Ways to Build Credit:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Get a clothing store account and pay it off monthly</li>
              <li>Put your cell phone contract in your name</li>
              <li>Start with a secured credit card</li>
              <li>Never miss a payment - set up debit orders</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Remember: It takes years to build good credit but only one mistake to damage it. 
            Treat your credit score like your reputation!
          </p>
        </>
      );
    }
    
    // ============ EMERGENCY FUND CONTENT ============
    if (lowerCourse.includes('emergency fund')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Why You Need an Emergency Fund</h4>
            <p className="text-[#A3A3A3] mb-4">
              An emergency fund is money set aside for unexpected expenses - car repairs, 
              medical bills, or job loss. It's your financial safety net that keeps you 
              from going into debt when life throws curveballs.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Emergency Fund Goals:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-yellow-400">Starter:</span> R5,000 - R10,000</li>
                <li><span className="text-[#C0975C]">Basic:</span> 1-2 months of expenses</li>
                <li><span className="text-green-400">Solid:</span> 3-6 months of expenses</li>
                <li><span className="text-[#C0975C]">Secure:</span> 6-12 months of expenses</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Start small - even R500/month adds up to R6,000 in a year. The goal is 
              progress, not perfection!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Your emergency fund should be easily accessible but separate from your 
            everyday spending money. This prevents you from "accidentally" spending it.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Where to Keep Your Emergency Fund:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>High-interest savings account (TymeBank, Discovery Bank)</li>
              <li>Money market account</li>
              <li>Separate bank account from your main one</li>
              <li>NOT in investments - needs to be accessible</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Rule: Only touch your emergency fund for TRUE emergencies - not sales, 
            holidays, or "treats". Your future self will thank you!
          </p>
        </>
      );
    }
    
    // ============ STOKVELS CONTENT ============
    if (lowerCourse.includes('stokvel')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What is a Stokvel?</h4>
            <p className="text-[#A3A3A3] mb-4">
              A stokvel is a traditional South African savings club where a group of people 
              contribute money regularly and take turns receiving the pool, or save together 
              for a common goal. It's community-powered saving that's been part of SA culture 
              for generations!
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Types of Stokvels:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-[#C0975C]">Rotating:</span> Each member gets the full pot in turn</li>
                <li><span className="text-[#0A1F44]">Savings:</span> Money saved for year-end payout</li>
                <li><span className="text-[#C0975C]">Grocery:</span> Pool money for bulk food buying</li>
                <li><span className="text-yellow-400">Investment:</span> Group invests together</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Stokvels manage over R50 billion annually in South Africa! It's a powerful 
              example of Ubuntu in action - we rise by lifting each other.
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Starting or joining a stokvel can help you save consistently through positive 
            peer pressure and community accountability.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Starting a Stokvel:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Find 5-12 trustworthy people</li>
              <li>Agree on contribution amount and frequency</li>
              <li>Write a constitution with rules</li>
              <li>Open a group bank account</li>
              <li>Meet regularly and keep records</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Many banks now offer stokvel accounts with special benefits. Check out 
            FNB Stokvel Account, Nedbank Stokvel, or Old Mutual Stokvel!
          </p>
        </>
      );
    }
    
    // ============ CREDIT CARDS CONTENT ============
    if (lowerCourse.includes('credit card')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Credit Cards: The Basics</h4>
            <p className="text-[#A3A3A3] mb-4">
              A credit card lets you borrow money up to a set limit to make purchases. 
              Used wisely, it's a powerful tool. Used poorly, it's a debt trap. The key 
              is understanding how they work.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Credit Card Terms You Must Know:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-[#C0975C]">Credit Limit:</span> Maximum you can borrow</li>
                <li><span className="text-[#0A1F44]">Interest Rate:</span> Cost of borrowing (15-25% in SA)</li>
                <li><span className="text-[#C0975C]">Minimum Payment:</span> Smallest amount due</li>
                <li><span className="text-yellow-400">Grace Period:</span> Interest-free days if paid in full</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Golden rule: If you pay your FULL balance every month, you pay ZERO interest. 
              That's how you win with credit cards!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Credit cards can be your friend or your enemy. The difference is how you use them.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Credit Card Do's and Don'ts:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Pay full balance monthly</li>
              <li>Use for budgeted expenses only</li>
              <li>Take advantage of rewards/cashback</li>
              <li>Don't pay only the minimum</li>
              <li>Don't use for emergencies (build a fund instead)</li>
              <li>Don't max out your limit</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Warning: Paying only the minimum on R10,000 at 20% interest could take 
            10+ years to pay off and cost R15,000+ in interest!
          </p>
        </>
      );
    }
    
    // ============ STUDENT LOANS / NSFAS CONTENT ============
    if (lowerCourse.includes('student loan') || lowerCourse.includes('nsfas')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Understanding NSFAS</h4>
            <p className="text-[#A3A3A3] mb-4">
              The National Student Financial Aid Scheme (NSFAS) provides funding for 
              students from poor and working-class families to attend universities and 
              TVET colleges. If your household income is under R350,000/year, you may qualify!
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">What NSFAS Covers:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>Tuition fees</li>
                <li>Accommodation (on or off campus)</li>
                <li>Living allowance for food and personal care</li>
                <li>Book allowance</li>
                <li>Transport allowance (for non-residence students)</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Important: NSFAS is now a BURSARY (not a loan) for qualifying students, 
              meaning you don't have to pay it back! Apply at nsfas.org.za
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Whether you're using NSFAS, a bank student loan, or family support, 
            managing your student finances well sets you up for success after graduation.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Student Money Tips:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Budget your allowance - don't blow it in week 1</li>
              <li>Look for part-time work or tutoring gigs</li>
              <li>Use student discounts everywhere</li>
              <li>If you have a loan, understand the repayment terms</li>
              <li>Start building credit responsibly</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Apply for NSFAS between September and January for the following year. 
            Don't miss the deadline!
          </p>
        </>
      );
    }
    
    // ============ AVOIDING SCAMS CONTENT ============
    if (lowerCourse.includes('scam')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Common Financial Scams in SA</h4>
            <p className="text-[#A3A3A3] mb-4">
              Financial scams cost South Africans billions of Rands every year. Scammers 
              are clever and constantly evolving their tactics. Knowing the common scams 
              helps you protect yourself and your money.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Red Flags to Watch For:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>"Get rich quick" promises</li>
                <li>Pressure to act NOW</li>
                <li>Requests for upfront fees</li>
                <li>Guaranteed high returns with no risk</li>
                <li>Unsolicited contact about "opportunities"</li>
                <li>Requests for your PIN or OTP</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Remember: If it sounds too good to be true, it IS. No legitimate investment 
              guarantees 50%+ returns!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Scammers target people through social media, WhatsApp, and even dating apps. 
            Stay alert and protect yourself.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Common Scams Targeting Youth:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li><span className="text-red-400">Forex/Crypto scams:</span> "I'll trade for you"</li>
              <li><span className="text-red-400">Pyramid schemes:</span> "Recruit 5 friends"</li>
              <li><span className="text-red-400">Fake job offers:</span> "Pay for training first"</li>
              <li><span className="text-red-400">Phishing:</span> Fake bank/SARS emails</li>
              <li><span className="text-red-400">Money mules:</span> "Just receive money for me"</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Never share your PIN, OTP, or banking password with ANYONE - not even someone 
            claiming to be from your bank!
          </p>
        </>
      );
    }
    
    // ============ CRYPTOCURRENCY CONTENT ============
    if (lowerCourse.includes('crypto')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What is Cryptocurrency?</h4>
            <p className="text-[#A3A3A3] mb-4">
              Cryptocurrency is digital money that uses cryptography for security. Unlike 
              Rands, it's not controlled by any government or bank. Bitcoin was the first 
              crypto, created in 2009, and now there are thousands of different cryptocurrencies.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Key Crypto Concepts:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-[#C0975C]">Blockchain:</span> The technology that records all transactions</li>
                <li><span className="text-[#0A1F44]">Bitcoin (BTC):</span> The first and largest cryptocurrency</li>
                <li><span className="text-[#C0975C]">Wallet:</span> Where you store your crypto</li>
                <li><span className="text-yellow-400">Exchange:</span> Where you buy/sell (Luno, VALR in SA)</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Warning: Crypto is VERY volatile. Only invest money you can afford to lose 
              completely. It's not a get-rich-quick scheme!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            If you want to explore crypto, do it safely and understand the risks involved.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Crypto Safety Rules:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Only use reputable SA exchanges (Luno, VALR, AltCoinTrader)</li>
              <li>Never invest more than 5-10% of your portfolio in crypto</li>
              <li>Don't chase "meme coins" or unknown tokens</li>
              <li>Secure your wallet with 2FA</li>
              <li>Keep your recovery phrase OFFLINE and secret</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Note: Crypto profits ARE taxable in South Africa. SARS treats it as an asset, 
            so keep records of all transactions!
          </p>
        </>
      );
    }
    
    // ============ ETFs CONTENT ============
    if (lowerCourse.includes('etf')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">What is an ETF?</h4>
            <p className="text-[#A3A3A3] mb-4">
              An ETF (Exchange-Traded Fund) is a basket of investments that trades on the 
              stock exchange like a single share. Instead of picking individual companies, 
              you buy one ETF and instantly own a piece of many companies!
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Why ETFs are Perfect for Beginners:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>Instant diversification - don't put all eggs in one basket</li>
                <li>Low fees compared to unit trusts</li>
                <li>No need to pick winning stocks</li>
                <li>Start from as little as R50 on EasyEquities</li>
                <li>Great for long-term wealth building</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Example: Buying the Satrix Top 40 ETF gives you ownership in the 40 biggest 
              companies on the JSE with one purchase!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            ETFs are considered one of the best ways for investors to build wealth 
            over time without needing expert knowledge.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Popular SA ETFs:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li><span className="text-[#C0975C]">Satrix 40:</span> Top 40 JSE companies</li>
              <li><span className="text-[#0A1F44]">Satrix MSCI World:</span> Global companies</li>
              <li><span className="text-[#C0975C]">Ashburton 1200:</span> Top 1200 global stocks</li>
              <li><span className="text-yellow-400">Satrix Property:</span> SA property companies</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Strategy: Put your TFSA money into a global ETF, set up a monthly debit order, 
            and don't touch it for 20+ years. That's how millionaires are made!
          </p>
        </>
      );
    }
    
    // ============ BUILDING WEALTH HABITS CONTENT ============
    if (lowerCourse.includes('wealth habit') || lowerCourse.includes('building wealth')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">The Wealth Mindset</h4>
            <p className="text-[#A3A3A3] mb-4">
              Building wealth isn't about how much you earn - it's about your habits. 
              Many high earners are broke because of bad habits, while average earners 
              become millionaires through consistency and discipline.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Wealth-Building Habits:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>Pay yourself FIRST (save before spending)</li>
                <li>Live below your means</li>
                <li>Invest consistently, regardless of market conditions</li>
                <li>Avoid lifestyle inflation when you earn more</li>
                <li>Continuously learn about money</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              The formula is simple: Earn → Save → Invest → Repeat. Start with 10% of 
              your income and increase it over time. Consistency beats intensity!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Small daily choices compound into massive results over time. Your 20s and 30s 
            are the most powerful wealth-building years because of compound interest.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">The Power of Starting Early:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>R500/month from age 20 → R3.2 million by 60</li>
              <li>R500/month from age 30 → R1.4 million by 60</li>
              <li>Starting 10 years earlier = R1.8 million more!</li>
              <li>Time is your biggest advantage - use it!</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Your future self will thank you for every Rand you save today. 
            Start now, start small, but START!
          </p>
        </>
      );
    }
    
    // ============ BUDGET CONTENT ============
    if (lowerCourse.includes('budget') || lowerCourse.includes('50/30/20') || lowerCourse.includes('cash flow')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Why Budgeting Matters</h4>
            <p className="text-[#A3A3A3] mb-4">
              A budget is simply a plan for your money. Without one, your money disappears 
              without you knowing where it went. With one, you control your financial future.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">The 50/30/20 Rule:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li><span className="text-[#C0975C]">50%</span> for Needs (rent, food, transport, utilities)</li>
                <li><span className="text-[#0A1F44]">30%</span> for Wants (entertainment, clothes, eating out)</li>
                <li><span className="text-[#C0975C]">20%</span> for Savings & Debt repayment</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Example: If you earn R10,000/month, aim for R5,000 on needs, R3,000 on wants, 
              and R2,000 towards savings or paying off debt. Adjust based on your situation!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Budgeting isn't about restricting yourself - it's about spending intentionally 
            on what matters to YOU while building wealth for your future.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Budgeting Apps for SA:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>22seven (free, links to SA banks)</li>
              <li>YNAB (You Need A Budget)</li>
              <li>Your bank's app spending tracker</li>
              <li>Even a simple spreadsheet works!</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Pro tip: Track every Rand for one month. You'll be shocked where your money 
            actually goes! Awareness is the first step to control.
          </p>
        </>
      );
    }
    
    // ============ INVESTING CONTENT ============
    if (lowerCourse.includes('invest') || lowerCourse.includes('jse') || lowerCourse.includes('stock')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Introduction to Investing</h4>
            <p className="text-[#A3A3A3] mb-4">
              Investing is how you make your money work for you. Instead of just saving, 
              you put your money into assets that can grow over time - like shares, 
              property, or ETFs.
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Key Concepts:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>The JSE (Johannesburg Stock Exchange) is SA's stock market</li>
                <li>Shares = ownership in a company</li>
                <li>Dividends = company profits shared with owners</li>
                <li>Capital gains = profit when you sell for more than you paid</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Remember: Investing is for the long term. The stock market goes up and down 
              daily, but historically grows over decades. Time in the market beats timing 
              the market!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            You don't need thousands of Rands to start investing. With platforms like 
            EasyEquities, you can buy fractional shares from just R10!
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Getting Started:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Open a TFSA first (tax-free growth)</li>
              <li>Start with broad ETFs, not individual stocks</li>
              <li>Set up automatic monthly investments</li>
              <li>Don't panic sell when markets drop</li>
              <li>Think decades, not days</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Warren Buffett's advice: "The stock market transfers money from the impatient 
            to the patient." Be patient!
          </p>
        </>
      );
    }
    
    // ============ BUSINESS / SIDE HUSTLE CONTENT ============
    if (lowerCourse.includes('business') || lowerCourse.includes('side business') || lowerCourse.includes('entrepreneur')) {
      if (lessonIndex === 0) {
        return (
          <>
            <h4 className="text-lg font-bold text-[#C0975C] mb-3">Starting Your Side Business</h4>
            <p className="text-[#A3A3A3] mb-4">
              A side business is a business you run alongside your main job or studies. 
              It's a great way to earn extra income, build skills, and maybe even create 
              your future full-time business!
            </p>
            <div className="bg-white/5 p-4 mb-4">
              <h5 className="font-bold mb-2">Side Business Ideas for South Africans:</h5>
              <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
                <li>Social media management</li>
                <li>Tutoring school subjects</li>
                <li>Graphic design / Canva work</li>
                <li>Reselling products online</li>
                <li>Content creation</li>
                <li>Photography / videography</li>
              </ul>
            </div>
            <p className="text-[#A3A3A3]">
              Start small and validate your idea before investing money. Many successful 
              businesses started as side businesss!
            </p>
          </>
        );
      }
      return (
        <>
          <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
          <p className="text-[#A3A3A3] mb-4">
            Running a business teaches you skills no classroom can - sales, marketing, 
            finance, and resilience. These skills are valuable even if you work for 
            someone else.
          </p>
          <div className="bg-white/5 p-4 mb-4">
            <h5 className="font-bold mb-2">Business Basics:</h5>
            <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
              <li>Validate your idea before spending money</li>
              <li>Keep business and personal money separate</li>
              <li>Track all income and expenses</li>
              <li>Register with CIPC if you grow</li>
              <li>Understand your tax obligations</li>
            </ul>
          </div>
          <p className="text-[#A3A3A3]">
            Remember: Every big business started somewhere. Your side business could be 
            the next big thing! Dala what you must!
          </p>
        </>
      );
    }
    
    // ============ DEFAULT CONTENT ============
    return (
      <>
        <h4 className="text-lg font-bold text-[#C0975C] mb-3">{lessonTitle}</h4>
        <p className="text-[#A3A3A3] mb-4">
          Welcome to this lesson on {lessonTitle.toLowerCase()}. This is an important 
          concept in your financial literacy journey that will help you make better 
          decisions with your money.
        </p>
        <div className="bg-white/5 p-4 mb-4">
          <h5 className="font-bold mb-2">What You'll Learn:</h5>
          <ul className="list-disc list-inside text-[#A3A3A3] space-y-1">
            <li>Core concepts and definitions</li>
            <li>How this applies to your financial life in SA</li>
            <li>Practical tips you can use today</li>
            <li>Common mistakes to avoid</li>
          </ul>
        </div>
        <p className="text-[#A3A3A3]">
          Take your time with this lesson. Financial literacy is a journey, not a 
          destination. Every concept you learn builds on the last!
        </p>
      </>
    );
  };
  
  return getContent();
}
